/**
 * Shared request scope for every `/api/analytics/*` call.
 *
 * - `auditYearId`: the backend limits each report to that audit year when
 *   set; absent means all years.
 * - `dataMode`: `demo` asks for a random stored demo variant instead of live
 *   data (only offered when the backend reports `widgets_demo_data`). Demo
 *   data ignores the audit year. `demoNonce` changes on every switch to Demo
 *   so a fresh random variant is fetched instead of the cached one.
 */
export type AnalyticsDataMode = 'live' | 'demo';

export type AnalyticsScope = {
  auditYearId?: string | null;
  dataMode?: AnalyticsDataMode;
  demoNonce?: string;
};

export function scopeParams(scope?: AnalyticsScope): Record<string, string> | undefined {
  if (scope?.dataMode === 'demo') {
    return { data: 'demo', demo_nonce: scope.demoNonce ?? '' };
  }
  return scope?.auditYearId ? { audit_year_id: scope.auditYearId } : undefined;
}

/**
 * Last element of every analytics query key: the audit year for live data
 * (`null` = all years, unchanged from before demo mode existed), or
 * `demo:<nonce>` for demo data, so demo and live results are cached apart.
 */
export function scopeKey(scope?: AnalyticsScope): string | null {
  if (scope?.dataMode === 'demo') return `demo:${scope.demoNonce ?? ''}`;
  return scope?.auditYearId ?? null;
}
